/**
 * EmailChangeModal - Modale per cambio email con verifica password
 *
 * Flusso articolato:
 * 1. Mostra confronto email attuale vs nuova
 * 2. Richiede password per conferma (solo auth_type='email')
 * 3. Invia richiesta al backend
 * 4. Mostra messaggio di successo con info su grace period 24h
 *
 * @author Salvo de Luca - Dialogicom s.r.l.s.
 * @version 1.0.0
 */

import React, { useState } from 'react';
import {
  Mail,
  Lock,
  AlertTriangle,
  CheckCircle,
  Loader2,
  X,
  ArrowRight,
  Clock
} from 'lucide-react';

interface EmailChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
  newEmail: string;
  userId: string;
  authType: 'email' | 'google' | string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

type ModalStep = 'confirm' | 'loading' | 'success' | 'error';

export const EmailChangeModal: React.FC<EmailChangeModalProps> = ({
  isOpen,
  onClose,
  currentEmail,
  newEmail,
  userId,
  authType,
  onSuccess,
  onError
}) => {
  const [step, setStep] = useState<ModalStep>('confirm');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const isGoogleAuth = authType === 'google';

  const handleSubmit = async () => {
    // Per utenti Google non serve password
    if (!isGoogleAuth && !password) {
      setErrorMessage('Inserisci la password per confermare');
      return;
    }

    setStep('loading');
    setErrorMessage('');

    try {
      const response = await fetch('/api/auth/request-email-change', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          currentEmail,
          newEmail,
          password: isGoogleAuth ? 'google-auth-skip' : password
        })
      });

      const data = await response.json();

      if (data.success) {
        setStep('success');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setStep('error');
        setErrorMessage(data.details || data.error || 'Errore durante la richiesta');
        if (onError) {
          onError(data.error);
        }
      }
    } catch (err) {
      setStep('error');
      setErrorMessage('Errore di connessione. Riprova più tardi.');
      if (onError) {
        onError('Errore di connessione');
      }
    }
  };

  const handleClose = () => {
    setStep('confirm');
    setPassword('');
    setErrorMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/70">
      <div className="bg-slate-900 rounded-xl w-full max-w-md border border-slate-700 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${step === 'error' ? 'bg-red-500/20' : step === 'success' ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
              {step === 'error' ? (
                <X className="w-5 h-5 text-red-400" />
              ) : step === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              )}
            </div>
            <h2 className="text-lg font-semibold text-white">
              {step === 'success' ? 'Email Inviata!' : step === 'error' ? 'Errore' : 'Cambio Email di Accesso'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {step === 'confirm' && (
            <>
              <p className="text-gray-300 mb-6">
                {isGoogleAuth
                  ? 'Stai modificando l\'email di contatto del tuo account.'
                  : 'Stai modificando l\'email associata al tuo account. Questa azione richiede verifica.'
                }
              </p>

              {/* Confronto email */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <Mail className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Email attuale</p>
                    <p className="text-white font-medium">{currentEmail}</p>
                  </div>
                </div>

                <div className="flex justify-center">
                  <ArrowRight className="w-5 h-5 text-gray-600" />
                </div>

                <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-xs text-blue-400 uppercase">Nuova email</p>
                    <p className="text-white font-medium">{newEmail}</p>
                  </div>
                </div>
              </div>

              {/* Password (solo per auth_type='email') */}
              {!isGoogleAuth && (
                <>
                  <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-200">
                      <p className="font-medium">Per sicurezza, dovrai:</p>
                      <ol className="list-decimal list-inside mt-1 text-amber-200/80">
                        <li>Confermare la tua password attuale</li>
                        <li>Verificare la nuova email (riceverai un link)</li>
                      </ol>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">
                      Password attuale
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="Inserisci la password"
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                      >
                        {showPassword ? 'Nascondi' : 'Mostra'}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Messaggio per Google auth */}
              {isGoogleAuth && (
                <div className="flex items-start gap-2 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30 mb-4">
                  <CheckCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-200">
                    Accedi con Google, quindi questa è solo un'email di contatto.
                    La modifica sarà applicata immediatamente.
                  </p>
                </div>
              )}

              {errorMessage && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 rounded-lg border border-red-500/30 mb-4">
                  <X className="w-5 h-5 text-red-400" />
                  <p className="text-sm text-red-300">{errorMessage}</p>
                </div>
              )}
            </>
          )}

          {step === 'loading' && (
            <div className="py-8 text-center">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-300">Elaborazione in corso...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="py-4">
              {isGoogleAuth ? (
                <>
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-green-500/20 rounded-full">
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                  </div>
                  <p className="text-center text-gray-300 mb-4">
                    La tua email di contatto è stata aggiornata con successo.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-green-500/20 rounded-full">
                      <Mail className="w-10 h-10 text-green-400" />
                    </div>
                  </div>
                  <p className="text-center text-gray-300 mb-4">
                    Abbiamo inviato un'email di verifica a:
                  </p>
                  <p className="text-center text-white font-semibold text-lg mb-4">
                    {newEmail}
                  </p>
                  <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-200">
                      <p className="font-medium">Hai 24 ore per confermare</p>
                      <p className="text-amber-200/80 mt-1">
                        Clicca sul link nell'email per completare il cambio.
                        Dopo 24 ore senza conferma, l'accesso potrebbe essere temporaneamente limitato.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'error' && (
            <div className="py-4">
              <div className="flex items-center justify-center mb-4">
                <div className="p-3 bg-red-500/20 rounded-full">
                  <X className="w-10 h-10 text-red-400" />
                </div>
              </div>
              <p className="text-center text-red-300 mb-2">
                {errorMessage}
              </p>
              <p className="text-center text-gray-500 text-sm">
                Verifica i dati e riprova.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-800 border-t border-slate-700 flex justify-end gap-3">
          {step === 'confirm' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg border border-slate-600 text-gray-300 hover:bg-slate-700 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                {isGoogleAuth ? 'Conferma Cambio' : 'Conferma e Invia Verifica'}
              </button>
            </>
          )}

          {(step === 'success' || step === 'error') && (
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
            >
              Chiudi
            </button>
          )}

          {step === 'error' && (
            <button
              onClick={() => setStep('confirm')}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors"
            >
              Riprova
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailChangeModal;
